<template>
  <AppLayout>
    <div class="p-4 md:p-6">

      <!-- ── DESKTOP HEADER ── -->
      <div class="hidden md:flex items-center justify-between mb-2">
        <div class="flex items-center gap-3">
          <a :href="apiPath"
            class="flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800
                   text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition shrink-0">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"/>
            </svg>
          </a>
          <div>
            <h1 class="text-2xl font-bold text-slate-800 dark:text-white">{{ expense?.name }}</h1>
            <p class="text-slate-500 dark:text-slate-400 text-sm mt-0.5">
              <span v-if="loading" class="inline-block w-24 h-3 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse"></span>
              <span v-else>{{ histories.length }} entr{{ histories.length !== 1 ? 'ies' : 'y' }}</span>
            </p>
          </div>
        </div>
        <button @click="openAdd"
          :class="['flex items-center gap-2 active:scale-95 text-white text-sm font-semibold px-4 py-2.5 rounded-xl shadow-sm transition', btnClass]">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
          </svg>
          Add Entry
        </button>
      </div>

      <!-- ── MOBILE HEADER ── -->
      <div class="md:hidden -mx-4 -mt-4 mb-1">
        <div :class="['px-4 pt-3 pb-6', gradientClass]">
          <a :href="apiPath"
            class="inline-flex items-center gap-1.5 text-white/80 text-xs font-semibold mb-3 hover:text-white transition">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"/>
            </svg>
            Back to {{ typeLabelPlural }}
          </a>
          <div class="flex items-center justify-between mb-3">
            <div>
              <h1 class="text-xl font-bold text-white">{{ expense?.name }}</h1>
              <p :class="['text-xs mt-0.5', textColorClass]">
                <span v-if="loading">Loading...</span>
                <span v-else>{{ histories.length }} entr{{ histories.length !== 1 ? 'ies' : 'y' }}</span>
              </p>
            </div>
            <button @click="openAdd"
              class="flex items-center gap-1.5 bg-white/20 active:bg-white/30 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
              </svg>
              Add
            </button>
          </div>
          <!-- Stats -->
          <div v-if="!loading" :class="hasMultipleCurrencies ? 'grid-cols-3' : 'grid-cols-2'" class="grid gap-2 mb-2">
            <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
              <p :class="['text-[9px] font-bold uppercase tracking-widest mb-1', textColorClass]">Entries</p>
              <p class="text-sm font-bold text-white">{{ histories.length }}</p>
            </div>
            <div v-for="(total, curr) in currencyTotals" :key="curr" class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
              <p class="text-[9px] font-bold text-amber-200 uppercase tracking-widest mb-1">{{ curr.toUpperCase() }}</p>
              <p class="text-xs font-bold text-white">{{ getCurrencySymbol(curr) }} {{ formatWholeNum(total) }}</p>
            </div>
            <div v-if="hasMultipleCurrencies" class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
              <p class="text-[9px] font-bold text-sky-200 uppercase tracking-widest mb-1">Total PKR</p>
              <p class="text-xs font-bold text-white">Rs {{ formatWholeNum(totalInPkr) }}</p>
            </div>
          </div>
          <div v-else class="grid grid-cols-2 gap-2 mb-2">
            <div v-for="i in 2" :key="i" class="bg-white/15 rounded-2xl h-14 animate-pulse"></div>
          </div>
        </div>

        <!-- Search card pulls over hero -->
        <div class="bg-gray-50 dark:bg-slate-950 rounded-t-3xl -mt-4 px-4 pt-3 pb-2">
          <div class="relative">
            <svg class="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"
              fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607z"/>
            </svg>
            <input v-model="search" type="text" placeholder="Search entries..."
              class="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                     text-slate-800 dark:text-white placeholder-slate-400 rounded-2xl pl-10 pr-10 py-3 text-sm
                     focus:outline-none focus:ring-2 focus:ring-rose-500 shadow-sm transition" />
            <button v-if="search" @click="search = ''"
              class="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- ── DESKTOP: Search + Stats ── -->
      <div class="hidden md:flex items-center gap-3 mb-3">
        <!-- Search -->
        <div class="relative w-56 shrink-0">
          <svg class="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607z"/>
          </svg>
          <input v-model="search" type="text" placeholder="Search..." :disabled="loading"
            class="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                   text-slate-800 dark:text-white placeholder-slate-400 rounded-xl pl-10 pr-10 py-2.5 text-sm
                   focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent transition disabled:opacity-50" />
          <button v-if="search" @click="search = ''"
            class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div class="flex-1"></div>

        <!-- Stats -->
        <div class="hidden md:flex items-center gap-2">
          <template v-if="loading">
            <div v-for="i in 3" :key="i" class="h-10 w-28 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse"></div>
          </template>
          <template v-else>
            <div :class="['rounded-xl px-4 py-2 text-right', statBgClass]">
              <p :class="['text-[10px] font-semibold uppercase tracking-wide', statLabelClass]">Entries</p>
              <p :class="['text-sm font-bold', statTextClass]">{{ histories.length }}</p>
            </div>
            <div v-for="(total, curr) in currencyTotals" :key="curr" class="bg-amber-50 dark:bg-amber-950/40 border border-amber-100 dark:border-amber-900 rounded-xl px-4 py-2 text-right">
              <p class="text-[10px] font-semibold text-amber-600 dark:text-amber-500 uppercase tracking-wide">{{ curr.toUpperCase() }}</p>
              <p class="text-sm font-bold text-amber-700 dark:text-amber-400">{{ getCurrencySymbol(curr) }} {{ formatWholeNum(total) }}</p>
            </div>
            <div v-if="hasMultipleCurrencies" class="bg-sky-50 dark:bg-sky-950/40 border border-sky-100 dark:border-sky-900 rounded-xl px-4 py-2 text-right">
              <p class="text-[10px] font-semibold text-sky-600 dark:text-sky-500 uppercase tracking-wide">Total PKR</p>
              <p class="text-sm font-bold text-sky-700 dark:text-sky-400">Rs {{ formatWholeNum(totalInPkr) }}</p>
            </div>
          </template>
        </div>
      </div>

      <!-- ── FLASH ── -->
      <div v-if="$page.props.flash?.notice"
        class="mb-4 flex items-center gap-2 bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400 text-sm px-4 py-3 rounded-xl">
        <svg class="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        {{ $page.props.flash.notice }}
      </div>
      <div v-if="$page.props.flash?.alert || errors?.length"
        class="mb-4 bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 text-sm px-4 py-3 rounded-xl">
        <p v-if="$page.props.flash?.alert">• {{ $page.props.flash.alert }}</p>
        <p v-for="e in (errors || [])" :key="e">• {{ e }}</p>
      </div>

      <!-- ── SKELETON ── -->
      <template v-if="loading">
        <!-- Desktop -->
        <div class="hidden md:block bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">
          <div class="border-b border-slate-100 dark:border-slate-800 px-5 py-3.5 flex gap-6">
            <div v-for="i in 5" :key="i" class="h-2.5 w-16 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
          </div>
          <div v-for="i in 6" :key="i" class="flex items-center gap-6 px-5 py-4 border-b border-slate-50 dark:border-slate-800/50">
            <div class="h-9 w-9 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse shrink-0"></div>
            <div class="h-3 w-36 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse flex-1"></div>
            <div class="h-3 w-24 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse ml-auto"></div>
          </div>
        </div>
        <!-- Mobile -->
        <div class="md:hidden space-y-3">
          <div v-for="i in 5" :key="i"
            class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4 space-y-3">
            <div class="flex items-center justify-between">
              <div class="h-4 w-36 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
            </div>
            <div class="grid grid-cols-2 gap-2">
              <div v-for="j in 2" :key="j" class="h-14 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse"></div>
            </div>
          </div>
        </div>
      </template>

      <!-- ── EMPTY STATE ── -->
      <div v-else-if="filtered.length === 0"
        class="flex flex-col items-center justify-center py-20 text-center">
        <div class="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
          <svg class="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z"/>
          </svg>
        </div>
        <p class="text-slate-600 dark:text-slate-400 font-medium">
          {{ search ? 'No entries match your search' : 'No expense entries yet' }}
        </p>
        <p class="text-slate-400 dark:text-slate-500 text-sm mt-1">
          {{ search ? 'Try a different keyword' : 'Add your first expense entry to get started' }}
        </p>
        <button v-if="!search" @click="openAdd" :class="['mt-4 text-sm font-semibold hover:text-rose-400 transition', statLabelClass]">+ Add Entry</button>
        <button v-else @click="search = ''" :class="['mt-4 text-sm font-semibold hover:text-rose-400 transition', statLabelClass]">Clear search</button>
      </div>

      <!-- ── DESKTOP TABLE ── -->
      <div v-else class="hidden md:block bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-visible">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-slate-100 dark:border-slate-800">
              <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide w-10">#</th>
              <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Date</th>
              <th class="text-right px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Amount</th>
              <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Payment</th>
              <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Notes</th>
              <th class="text-center px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Actions</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(history, i) in filtered" :key="history.id">
              <tr @click="toggleExpandDesktop(history.id)"
                :class="['border-t border-slate-50 dark:border-slate-800 transition cursor-pointer', hoverBgClass]">
                <td class="px-5 py-4 text-slate-400 text-xs">
                  <div class="flex items-center gap-2">
                    <svg class="w-3.5 h-3.5 transition-transform duration-200"
                      :class="expandedDesktopId === history.id ? ['rotate-90', statLabelClass] : ''"
                      fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5"/>
                    </svg>
                    <span>{{ i + 1 }}</span>
                  </div>
                </td>
                <td class="px-5 py-4">
                  <div class="flex items-center gap-2.5">
                    <div :class="['w-8 h-8 rounded-xl flex items-center justify-center shrink-0', iconBgClass]">
                      <svg :class="['w-4 h-4', iconColorClass]" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"/>
                      </svg>
                    </div>
                    <span class="font-semibold text-slate-800 dark:text-white">{{ history.target_date }}</span>
                  </div>
                </td>
                <td :class="['px-5 py-4 text-right font-semibold', statTextClass]">{{ getCurrencySymbol(history.currency) }} {{ formatNum(history.amount) }}</td>
                <td class="px-5 py-4">
                  <div class="flex items-center gap-2">
                    <span v-if="history.is_cash" class="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400">
                      Cash
                    </span>
                    <span v-else class="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400">
                      {{ history.bank_name || 'Bank' }}
                    </span>
                    <span v-if="history.has_proof" @click.stop="openProof(history.proof_url)" title="View payment proof"
                      class="inline-flex items-center justify-center w-6 h-6 rounded bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900 transition cursor-pointer">
                      <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"/>
                      </svg>
                    </span>
                  </div>
                </td>
                <td class="px-5 py-4 text-slate-500 dark:text-slate-400 text-sm max-w-xs truncate">
                  {{ history.notes || '—' }}
                </td>
                <td class="px-5 py-4 text-center" @click.stop>
                  <div class="flex items-center justify-center gap-1">
                    <button @click="history.bank_deleted ? null : openDuplicate(history)"
                      :disabled="history.bank_deleted"
                      :class="[
                        'inline-flex items-center justify-center w-8 h-8 rounded-lg transition relative group',
                        history.bank_deleted
                          ? 'text-slate-300 dark:text-slate-600 cursor-not-allowed'
                          : 'text-blue-500 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950'
                      ]"
                      :title="history.bank_deleted ? `${history.bank_name} no longer exists, probably deleted` : 'Duplicate entry'">
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75"/>
                      </svg>
                      <svg v-if="history.bank_deleted" class="w-3 h-3 absolute -top-0.5 -right-0.5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd"/>
                      </svg>
                    </button>
                    <button @click="history.bank_deleted ? null : openEdit(history)"
                      :disabled="history.bank_deleted"
                      :class="[
                        'inline-flex items-center justify-center w-8 h-8 rounded-lg transition relative group',
                        history.bank_deleted
                          ? 'text-slate-300 dark:text-slate-600 cursor-not-allowed'
                          : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                      ]"
                      :title="history.bank_deleted ? `${history.bank_name} no longer exists, probably deleted` : 'Edit entry'">
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z"/>
                      </svg>
                      <svg v-if="history.bank_deleted" class="w-3 h-3 absolute -top-0.5 -right-0.5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd"/>
                      </svg>
                    </button>
                    <button @click="openDeleteConfirm(history)"
                      class="inline-flex items-center justify-center w-8 h-8 text-red-400
                             hover:bg-red-50 dark:hover:bg-red-950 rounded-lg transition"
                      title="Delete entry">
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>

              <!-- Expanded details row -->
              <tr v-if="expandedDesktopId === history.id">
                <td colspan="6" class="px-5 py-4 bg-slate-50 dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800">
                  <div class="flex gap-4">
                    <!-- Payment Proof -->
                    <div v-if="history.has_proof" class="flex-shrink-0">
                      <p class="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Payment Proof</p>
                      <div @click="openProof(history.proof_url)"
                        class="cursor-pointer rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden hover:border-blue-500 dark:hover:border-blue-500 transition">
                        <img :src="history.proof_url" alt="Payment proof" class="w-64 h-auto" />
                      </div>
                    </div>

                    <!-- Notes (if any) -->
                    <div v-if="history.notes" class="flex-1">
                      <p class="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Notes</p>
                      <div class="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 px-4 py-3">
                        <p class="text-sm text-slate-700 dark:text-slate-300">{{ history.notes }}</p>
                      </div>
                    </div>

                    <!-- Empty state if no proof and no notes -->
                    <div v-if="!history.has_proof && !history.notes" class="flex-1 text-center py-8">
                      <p class="text-sm text-slate-400">No additional details available</p>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
          <tfoot>
            <tr v-for="(total, curr, idx) in currencyTotals" :key="curr"
              :class="idx === 0 ? 'border-t-2 border-slate-100 dark:border-slate-800' : 'border-t border-slate-100 dark:border-slate-800'"
              class="bg-slate-50 dark:bg-slate-800/50">
              <td colspan="2" class="px-5 py-3.5">
                <span v-if="idx === 0" class="text-xs font-semibold text-slate-500 dark:text-slate-400">
                  Total · {{ filtered.length }} {{ filtered.length === 1 ? 'entry' : 'entries' }}
                </span>
              </td>
              <td :class="['px-5 py-3.5 text-right text-sm font-bold', statTextClass]">
                {{ getCurrencySymbol(curr) }} {{ formatWholeNum(total) }}
                <span class="text-xs text-slate-400 ml-1">{{ curr.toUpperCase() }}</span>
              </td>
              <td colspan="3"></td>
            </tr>
            <tr v-if="hasMultipleCurrencies" class="border-t border-slate-100 dark:border-slate-800 bg-sky-50 dark:bg-sky-950/20">
              <td colspan="2" class="px-5 py-3.5">
                <span class="text-xs font-semibold text-sky-600 dark:text-sky-400">
                  Total in PKR
                </span>
              </td>
              <td class="px-5 py-3.5 text-right text-sm font-bold text-sky-600 dark:text-sky-400">
                Rs {{ formatWholeNum(totalInPkr) }}
              </td>
              <td colspan="3"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- ── MOBILE CARDS ── -->
      <div v-if="!loading" class="md:hidden space-y-3">
        <div v-for="(history, i) in filtered" :key="history.id"
          class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">
          <!-- Card header -->
          <div class="flex items-center justify-between px-4 py-3.5 cursor-pointer" @click="toggleExpand(history.id)">
            <div class="flex items-center gap-2.5 min-w-0">
              <svg class="w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform duration-200"
                :class="expandedId === history.id ? ['rotate-90', statLabelClass] : ''"
                fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5"/>
              </svg>
              <div class="min-w-0">
                <p class="font-semibold text-slate-800 dark:text-white flex items-center gap-1.5">
                  {{ history.target_date }}
                  <svg v-if="history.has_proof" class="w-3.5 h-3.5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" title="Has proof">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"/>
                  </svg>
                </p>
                <p class="text-xs text-slate-400 mt-0.5">{{ history.is_cash ? 'Cash' : history.bank_name }}</p>
              </div>
            </div>
            <div class="text-right shrink-0 ml-3">
              <p :class="['text-sm font-bold', statTextClass]">{{ getCurrencySymbol(history.currency) }} {{ formatNum(history.amount) }}</p>
            </div>
          </div>

          <!-- Expanded details -->
          <div v-if="expandedId === history.id"
            class="border-t border-rose-100 dark:border-rose-900/30 bg-rose-50/30 dark:bg-rose-950/10 p-4 space-y-3">
            <!-- Proof image -->
            <div v-if="history.has_proof" @click="openProof(history.proof_url)"
              class="block bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 transition">
              <p class="text-[10px] text-slate-400 uppercase tracking-wide mb-2">Payment Proof</p>
              <img :src="history.proof_url" alt="Payment proof" class="w-full h-auto rounded-lg" />
            </div>

            <!-- Info card -->
            <div v-if="history.notes" class="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-3">
              <p class="text-[10px] text-slate-400 uppercase tracking-wide mb-1">Notes</p>
              <p class="text-sm text-slate-700 dark:text-slate-300">{{ history.notes }}</p>
            </div>

            <!-- Action buttons -->
            <div class="grid grid-cols-3 gap-2">
              <button @click.stop="history.bank_deleted ? null : openDuplicate(history)"
                :disabled="history.bank_deleted"
                :class="[
                  'flex items-center justify-center gap-1.5 text-sm font-semibold rounded-xl py-2.5 transition relative',
                  history.bank_deleted
                    ? 'text-slate-400 dark:text-slate-600 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 cursor-not-allowed'
                    : 'text-blue-600 dark:text-blue-400 bg-white dark:bg-slate-900 border border-blue-200 dark:border-blue-800 active:scale-95'
                ]"
                :title="history.bank_deleted ? `${history.bank_name} no longer exists, probably deleted` : 'Duplicate entry'">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75"/>
                </svg>
                <svg v-if="history.bank_deleted" class="w-3 h-3 absolute top-1 right-1 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd"/>
                </svg>
                <span class="hidden sm:inline">Duplicate</span>
              </button>
              <button @click.stop="history.bank_deleted ? null : openEdit(history)"
                :disabled="history.bank_deleted"
                :class="[
                  'flex items-center justify-center gap-1.5 text-sm font-semibold rounded-xl py-2.5 transition relative',
                  history.bank_deleted
                    ? 'text-slate-400 dark:text-slate-600 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 cursor-not-allowed'
                    : 'text-indigo-600 dark:text-indigo-400 bg-white dark:bg-slate-900 border border-indigo-200 dark:border-indigo-800 active:scale-95'
                ]"
                :title="history.bank_deleted ? `${history.bank_name} no longer exists, probably deleted` : 'Edit entry'">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z"/>
                </svg>
                <svg v-if="history.bank_deleted" class="w-3 h-3 absolute top-1 right-1 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd"/>
                </svg>
                <span class="hidden sm:inline">Edit</span>
              </button>
              <button @click.stop="openDeleteConfirm(history)"
                class="flex items-center justify-center gap-1.5 text-sm font-semibold text-red-600 dark:text-red-400
                       bg-white dark:bg-slate-900 border border-red-200 dark:border-red-800 rounded-xl py-2.5 transition active:scale-95">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"/>
                </svg>
                <span class="hidden sm:inline">Delete</span>
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- ── ADD MODAL ── -->
    <Modal v-if="showAdd" @close="closeAdd">
      <template #title>Add {{ typeLabel }} Entry</template>
      <form @submit.prevent="submitAdd" class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Date</label>
          <input v-model="addForm.target_date" type="date" required
            class="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                   text-slate-800 dark:text-white rounded-xl px-4 py-3 text-sm
                   focus:outline-none focus:ring-2 focus:border-transparent transition"
            :class="ringClass" />
        </div>
        <!-- Amount with Currency -->
        <div>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Amount</label>
          <div class="flex items-center bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden focus-within:ring-2 transition"
            :class="focusWithinRingClass">
            <span class="px-3 text-sm font-semibold text-slate-400 dark:text-slate-500 shrink-0">{{ addFormCurrency }}</span>
            <input v-model="addForm.amount" type="number" step="0.01" placeholder="0.00" required
              class="flex-1 bg-transparent text-slate-800 dark:text-white placeholder-slate-400 py-3 pr-4 text-sm focus:outline-none" />
          </div>
        </div>

        <!-- Payment Type -->
        <div>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Payment Method</label>
          <div class="grid grid-cols-2 gap-2">
            <button type="button" @click="addForm.is_cash = true; addForm.bank_id = null"
              class="flex items-center justify-center gap-2 py-3 rounded-xl border-2 text-sm font-semibold transition"
              :class="addForm.is_cash
                ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400'
                : 'border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600'">
              Cash
            </button>
            <button type="button" @click="addForm.is_cash = false"
              class="flex items-center justify-center gap-2 py-3 rounded-xl border-2 text-sm font-semibold transition"
              :class="!addForm.is_cash
                ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400'
                : 'border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600'">
              Bank
            </button>
          </div>
        </div>

        <!-- Bank Selection -->
        <div v-if="!addForm.is_cash">
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Select Bank</label>
          <select v-model="addForm.bank_id" required
            class="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                   text-slate-800 dark:text-white rounded-xl px-4 py-3 text-sm
                   focus:outline-none focus:ring-2 focus:border-transparent transition"
            :class="ringClass">
            <option value="">Choose a bank...</option>
            <option v-for="bank in banks" :key="bank.id" :value="bank.id">{{ bank.name }}</option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Notes (Optional)</label>
          <textarea v-model="addForm.notes" rows="3" placeholder="Add any notes..."
            class="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                   text-slate-800 dark:text-white placeholder-slate-400 rounded-xl px-4 py-3 text-sm
                   focus:outline-none focus:ring-2 focus:border-transparent transition resize-none"
            :class="ringClass"></textarea>
        </div>

        <!-- Proof Upload -->
        <div>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Payment Proof (Optional)</label>

          <div v-if="addProofPreview" class="mb-3 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
            <div class="flex items-start gap-3">
              <div class="flex-shrink-0">
                <img :src="addProofPreview" alt="Proof preview"
                  class="w-24 h-24 object-cover rounded-lg shadow-md ring-2 ring-white dark:ring-slate-700" />
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-start justify-between gap-2">
                  <div class="flex-1">
                    <p class="text-sm font-semibold text-slate-700 dark:text-slate-300">Preview</p>
                    <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Image ready to upload</p>
                    <div class="mt-2 inline-flex items-center gap-1.5 px-2 py-1 bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400 rounded-md text-xs font-medium">
                      <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      Compressed & Ready
                    </div>
                  </div>
                  <button type="button" @click="removeAddProof"
                    class="flex items-center justify-center w-8 h-8 bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900 transition-colors">
                    <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="relative">
            <input type="file" @change="handleAddProofChange" accept="image/*" id="add-proof-input"
              class="hidden" />
            <label for="add-proof-input"
              class="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl cursor-pointer bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
              <div class="flex flex-col items-center justify-center pt-5 pb-6">
                <svg class="w-8 h-8 mb-2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"/>
                </svg>
                <p class="mb-1 text-sm text-slate-600 dark:text-slate-300 font-medium">
                  <span class="text-indigo-600 dark:text-indigo-400">Click to upload</span> or drag and drop
                </p>
                <p class="text-xs text-slate-500 dark:text-slate-400">PNG, JPG, JPEG (Auto-compressed)</p>
              </div>
            </label>
          </div>
        </div>

        <div class="flex gap-3 pt-2">
          <button type="button" @click="closeAdd"
            class="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-medium
                   text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition">
            Cancel
          </button>
          <button type="submit" :disabled="addLoading"
            :class="['flex-1 py-2.5 rounded-xl disabled:opacity-60 text-white text-sm font-semibold transition', btnClass.split(' ')[0] + ' ' + btnClass.split(' ')[1]]">
            {{ addLoading ? 'Adding...' : 'Add Entry' }}
          </button>
        </div>
      </form>
    </Modal>

    <!-- ── EDIT MODAL ── -->
    <Modal v-if="showEdit" @close="closeEdit">
      <template #title>Edit {{ typeLabel }} Entry</template>
      <form @submit.prevent="submitEdit" class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Date</label>
          <input v-model="editForm.target_date" type="date" required
            class="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                   text-slate-800 dark:text-white rounded-xl px-4 py-3 text-sm
                   focus:outline-none focus:ring-2 focus:border-transparent transition"
            :class="ringClass" />
        </div>
        <!-- Amount with Currency -->
        <div>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Amount</label>
          <div class="flex items-center bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden focus-within:ring-2 transition"
            :class="focusWithinRingClass">
            <span class="px-3 text-sm font-semibold text-slate-400 dark:text-slate-500 shrink-0">{{ editFormCurrency }}</span>
            <input v-model="editForm.amount" type="number" step="0.01" placeholder="0.00" required
              class="flex-1 bg-transparent text-slate-800 dark:text-white placeholder-slate-400 py-3 pr-4 text-sm focus:outline-none" />
          </div>
        </div>

        <!-- Payment Type -->
        <div>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Payment Method</label>
          <div class="grid grid-cols-2 gap-2">
            <button type="button" @click="editForm.is_cash = true; editForm.bank_id = null"
              class="flex items-center justify-center gap-2 py-3 rounded-xl border-2 text-sm font-semibold transition"
              :class="editForm.is_cash
                ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400'
                : 'border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600'">
              Cash
            </button>
            <button type="button" @click="editForm.is_cash = false"
              class="flex items-center justify-center gap-2 py-3 rounded-xl border-2 text-sm font-semibold transition"
              :class="!editForm.is_cash
                ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400'
                : 'border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600'">
              Bank
            </button>
          </div>
        </div>

        <!-- Bank Selection -->
        <div v-if="!editForm.is_cash">
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Select Bank</label>
          <select v-model="editForm.bank_id" required
            class="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                   text-slate-800 dark:text-white rounded-xl px-4 py-3 text-sm
                   focus:outline-none focus:ring-2 focus:border-transparent transition"
            :class="ringClass">
            <option value="">Choose a bank...</option>
            <option v-for="bank in banks" :key="bank.id" :value="bank.id">{{ bank.name }}</option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Notes (Optional)</label>
          <textarea v-model="editForm.notes" rows="3" placeholder="Add any notes..."
            class="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                   text-slate-800 dark:text-white placeholder-slate-400 rounded-xl px-4 py-3 text-sm
                   focus:outline-none focus:ring-2 focus:border-transparent transition resize-none"
            :class="ringClass"></textarea>
        </div>

        <!-- Proof Upload -->
        <div>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Payment Proof (Optional)</label>

          <div v-if="editProofPreview" class="mb-3 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
            <div class="flex items-start gap-3">
              <div class="flex-shrink-0">
                <img :src="editProofPreview" alt="Proof preview"
                  class="w-24 h-24 object-cover rounded-lg shadow-md ring-2 ring-white dark:ring-slate-700" />
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-start justify-between gap-2">
                  <div class="flex-1">
                    <p class="text-sm font-semibold text-slate-700 dark:text-slate-300">
                      {{ editProofFile ? 'New Preview' : 'Current Proof' }}
                    </p>
                    <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {{ editProofFile ? 'Image ready to upload' : 'Existing payment proof' }}
                    </p>
                    <div v-if="editProofFile" class="mt-2 inline-flex items-center gap-1.5 px-2 py-1 bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400 rounded-md text-xs font-medium">
                      <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      Compressed & Ready
                    </div>
                    <div v-else class="mt-2 inline-flex items-center gap-1.5 px-2 py-1 bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-400 rounded-md text-xs font-medium">
                      <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244"/>
                      </svg>
                      Saved Proof
                    </div>
                  </div>
                  <button type="button" @click="removeEditProof"
                    class="flex items-center justify-center w-8 h-8 bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900 transition-colors">
                    <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="relative">
            <input type="file" @change="handleEditProofChange" accept="image/*" id="edit-proof-input"
              class="hidden" />
            <label for="edit-proof-input"
              class="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl cursor-pointer bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
              <div class="flex flex-col items-center justify-center pt-5 pb-6">
                <svg class="w-8 h-8 mb-2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"/>
                </svg>
                <p class="mb-1 text-sm text-slate-600 dark:text-slate-300 font-medium">
                  <span class="text-indigo-600 dark:text-indigo-400">Click to upload</span> or drag and drop
                </p>
                <p class="text-xs text-slate-500 dark:text-slate-400">PNG, JPG, JPEG (Auto-compressed)</p>
              </div>
            </label>
          </div>
        </div>

        <div class="flex gap-3 pt-2">
          <button type="button" @click="closeEdit"
            class="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-medium
                   text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition">
            Cancel
          </button>
          <button type="submit" :disabled="editLoading"
            :class="['flex-1 py-2.5 rounded-xl disabled:opacity-60 text-white text-sm font-semibold transition', btnClass.split(' ')[0] + ' ' + btnClass.split(' ')[1]]">
            {{ editLoading ? 'Saving...' : 'Save Changes' }}
          </button>
        </div>
      </form>
    </Modal>

    <!-- ── DELETE CONFIRM ── -->
    <ConfirmDialog
      v-if="showDeleteConfirm"
      :title="`Delete ${typeLabel} Entry?`"
      :message="`Are you sure you want to permanently delete this ${typeLabel.toLowerCase()} entry?`"
      :info-message="`WARNING: This will permanently delete this ${typeLabel.toLowerCase()} history entry. This action cannot be undone and the data will be lost forever.`"
      confirm-text="Delete Forever"
      loading-text="Deleting..."
      variant="danger"
      @confirm="confirmDelete"
      @cancel="closeDeleteConfirm"
    />

  </AppLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useForm, usePage, router } from '@inertiajs/vue3'
import imageCompression from 'browser-image-compression'
import AppLayout from '@/layouts/AppLayout.vue'
import Modal from '@/components/Modal.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'

const page = usePage()
const errors = computed(() => page.props.errors)
const businessName = computed(() => page.props.auth?.user?.business_name?.trim() || 'Master Ledger')

// Get the record from either income or expense prop
const expense = computed(() => page.props.income || page.props.expense)

// ── Type Detection (Expense vs Income) ──────────────────────
const isIncome = computed(() => page.url.startsWith('/incomes'))
const type = computed(() => isIncome.value ? 'income' : 'expense')
const typePlural = computed(() => isIncome.value ? 'incomes' : 'expenses')
const typeLabel = computed(() => isIncome.value ? 'Income' : 'Expense')
const typeLabelPlural = computed(() => isIncome.value ? 'Incomes' : 'Expenses')
const apiPath = computed(() => `/${typePlural.value}`)

// Dynamic color classes
const btnClass = computed(() => isIncome.value
  ? 'bg-emerald-500 hover:bg-emerald-400 shadow-emerald-500/30'
  : 'bg-rose-500 hover:bg-rose-400 shadow-rose-500/30')
const gradientClass = computed(() => isIncome.value
  ? 'bg-gradient-to-br from-emerald-600 via-emerald-500 to-teal-500'
  : 'bg-gradient-to-br from-rose-600 via-rose-500 to-pink-500')
const textColorClass = computed(() => isIncome.value ? 'text-emerald-200' : 'text-rose-200')
const ringClass = computed(() => isIncome.value ? 'focus:ring-emerald-500' : 'focus:ring-rose-500')
const focusWithinRingClass = computed(() => isIncome.value ? 'focus-within:ring-emerald-500' : 'focus-within:ring-rose-500')
const iconBgClass = computed(() => isIncome.value ? 'bg-emerald-100 dark:bg-emerald-950' : 'bg-rose-100 dark:bg-rose-950')
const iconColorClass = computed(() => isIncome.value ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400')
const hoverBgClass = computed(() => isIncome.value ? 'hover:bg-emerald-50 dark:hover:bg-emerald-950/20' : 'hover:bg-rose-50 dark:hover:bg-rose-950/20')
const statBgClass = computed(() => isIncome.value
  ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-100 dark:border-emerald-900'
  : 'bg-rose-50 dark:bg-rose-950/40 border-rose-100 dark:border-rose-900')
const statTextClass = computed(() => isIncome.value ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400')
const statLabelClass = computed(() => isIncome.value ? 'text-emerald-500' : 'text-rose-500')
const expandedBgClass = computed(() => isIncome.value
  ? 'border-t border-emerald-100 dark:border-emerald-900/30 bg-emerald-50/30 dark:bg-emerald-950/10'
  : 'border-t border-rose-100 dark:border-rose-900/30 bg-rose-50/30 dark:bg-rose-950/10')

const histories = ref([])
const banks = ref([])
const loading = ref(true)

// ── Currency helpers ─────────────────────────────────────
function getCurrencySymbol(currency) {
  const symbols = { pkr: 'Rs', usd: '$', euro: '€' }
  return symbols[currency] || 'Rs'
}

// ── Totals by currency ───────────────────────────────────
const currencyTotals = computed(() => {
  const totals = {}
  filtered.value.forEach(h => {
    const curr = h.currency || 'pkr'
    if (!totals[curr]) totals[curr] = 0
    totals[curr] += parseFloat(h.amount || 0)
  })
  return totals
})

const totalInPkr = computed(() => {
  return filtered.value.reduce((sum, h) => {
    const amount = parseFloat(h.amount || 0)
    const rate = parseFloat(h.rate_in_pkr || 1)
    return sum + (amount * rate)
  }, 0)
})

const hasMultipleCurrencies = computed(() => Object.keys(currencyTotals.value).length > 1)

const totalAmount = computed(() => {
  return filtered.value.reduce((sum, h) => sum + parseFloat(h.amount || 0), 0)
})

// ── Search ───────────────────────────────────────────────
const search = ref('')
const filtered = computed(() => {
  const q = search.value.trim().toLowerCase()
  return q ? histories.value.filter(h =>
    h.target_date.toLowerCase().includes(q) ||
    h.notes?.toLowerCase().includes(q) ||
    h.bank_name?.toLowerCase().includes(q)
  ) : [...histories.value]
})

// ── Expand (Mobile) ──────────────────────────────────────
const expandedId = ref(null)
function toggleExpand(id) {
  expandedId.value = expandedId.value === id ? null : id
}

// ── Expand (Desktop) ─────────────────────────────────────
const expandedDesktopId = ref(null)
function toggleExpandDesktop(id) {
  expandedDesktopId.value = expandedDesktopId.value === id ? null : id
}

// ── Fetch ────────────────────────────────────────────────
async function fetchData() {
  loading.value = true
  try {
    const res = await fetch(`${apiPath.value}/${expense.value.id}/histories/data`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
    const json = await res.json()
    histories.value = json.histories || []
    banks.value = json.banks || []
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  document.title = `${expense.value.name} | ${businessName.value}`
  fetchData()
})

// ── Utilities ────────────────────────────────────────────
function formatNum(n) {
  return Number(n).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatWholeNum(n) {
  return Math.floor(Number(n)).toLocaleString('en-PK')
}

// ── Open Proof (works in web and Android WebView) ────────
function openProof(url) {
  if (!url) {
    console.log('[openProof] No URL provided')
    return
  }

  console.log('[openProof] Opening URL:', url)
  console.log('[openProof] User agent:', navigator.userAgent)

  // Try multiple methods to ensure it works in Android WebView
  try {
    // Method 1: Direct window.open (simplest, should trigger onCreateWindow)
    console.log('[openProof] Attempting window.open...')
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
    console.log('[openProof] window.open returned:', newWindow)

    // Method 2: Create a temporary link and click it (fallback)
    if (!newWindow) {
      console.log('[openProof] window.open failed, trying link click...')
      const link = document.createElement('a')
      link.href = url
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      console.log('[openProof] Link click executed')
    }
  } catch (error) {
    console.error('[openProof] Error opening proof:', error)
    // Method 3: Last resort - direct navigation
    console.log('[openProof] All methods failed, trying direct navigation...')
    window.location.href = url
  }
}

// ── Image Compression ────────────────────────────────────
async function compressImage(file) {
  const options = {
    maxSizeMB: 0.5,
    maxWidthOrHeight: 1024,
    useWebWorker: false,
  }
  try {
    return await imageCompression(file, options)
  } catch (error) {
    console.error('Error compressing image:', error)
    return file
  }
}

// ── Add ──────────────────────────────────────────────────
const showAdd = ref(false)
const addLoading = ref(false)
const addForm = ref({ target_date: new Date().toISOString().split('T')[0], amount: '', notes: '', is_cash: true, bank_id: null })
const addProofFile = ref(null)
const addProofPreview = ref(null)

const addFormCurrency = computed(() => {
  if (addForm.value.is_cash) return 'Rs'
  const selectedBank = banks.value.find(b => b.id === addForm.value.bank_id)
  return selectedBank ? getCurrencySymbol(selectedBank.currency) : 'Rs'
})

function openAdd() {
  // Smart auto-fill logic
  if (histories.value.length > 0) {
    const lastEntry = histories.value[0] // histories are ordered by target_date desc

    // If only one entry, duplicate it completely
    if (histories.value.length === 1) {
      addForm.value = {
        target_date: new Date().toISOString().split('T')[0],
        amount: lastEntry.amount || '',
        notes: lastEntry.notes || '',
        is_cash: lastEntry.is_cash,
        bank_id: lastEntry.bank_id || null
      }
    } else {
      // Check if all entries have the same amount, notes, payment method, and bank
      const allSameAmount = histories.value.every(h => h.amount === lastEntry.amount)
      const allSameNotes = histories.value.every(h => h.notes === lastEntry.notes)
      const allSameCash = histories.value.every(h => h.is_cash === lastEntry.is_cash)
      const allSameBank = histories.value.every(h => h.bank_id === lastEntry.bank_id)

      if (allSameAmount && allSameNotes && allSameCash && allSameBank) {
        // All entries are identical, duplicate completely
        addForm.value = {
          target_date: new Date().toISOString().split('T')[0],
          amount: lastEntry.amount || '',
          notes: lastEntry.notes || '',
          is_cash: lastEntry.is_cash,
          bank_id: lastEntry.bank_id || null
        }
      } else {
        // Entries vary, only copy payment method and bank from last entry
        addForm.value = {
          target_date: new Date().toISOString().split('T')[0],
          amount: '',
          notes: '',
          is_cash: lastEntry.is_cash,
          bank_id: lastEntry.bank_id || null
        }
      }
    }
  }
  showAdd.value = true
}

async function handleAddProofChange(event) {
  const file = event.target.files[0]
  if (file) {
    addProofPreview.value = URL.createObjectURL(file)
    addProofFile.value = await compressImage(file)
  }
}

function removeAddProof() {
  addProofFile.value = null
  addProofPreview.value = null
}

function closeAdd() {
  showAdd.value = false
  addForm.value = { target_date: new Date().toISOString().split('T')[0], amount: '', notes: '', is_cash: true, bank_id: null }
  addProofFile.value = null
  addProofPreview.value = null
}

function submitAdd() {
  addLoading.value = true
  const payload = {
    ...addForm.value,
    amount: parseFloat(addForm.value.amount) || 0,
    bank_id: addForm.value.is_cash ? null : addForm.value.bank_id
  }
  if (addProofFile.value) {
    payload.proof = addProofFile.value
  }
  const formKey = `${type.value}_history`
  const formData = {}
  formData[formKey] = payload
  useForm(formData).post(`${apiPath.value}/${expense.value.id}/histories`, {
    onSuccess: () => { closeAdd(); fetchData() },
    onFinish: () => { addLoading.value = false },
  })
}

// ── Duplicate ────────────────────────────────────────────
function openDuplicate(history) {
  addForm.value = {
    target_date: new Date().toISOString().split('T')[0], // Today's date
    amount: history.amount || '',
    notes: history.notes || '',
    is_cash: history.is_cash,
    bank_id: history.bank_id || null
  }
  showAdd.value = true
}

// ── Edit ─────────────────────────────────────────────────
const showEdit = ref(false)
const editLoading = ref(false)
const editTarget = ref(null)
const editForm = ref({ target_date: '', amount: '', notes: '', is_cash: true, bank_id: null })
const editProofFile = ref(null)
const editProofPreview = ref(null)

const editFormCurrency = computed(() => {
  if (editForm.value.is_cash) return 'Rs'
  const selectedBank = banks.value.find(b => b.id === editForm.value.bank_id)
  return selectedBank ? getCurrencySymbol(selectedBank.currency) : 'Rs'
})

async function handleEditProofChange(event) {
  const file = event.target.files[0]
  if (file) {
    editProofPreview.value = URL.createObjectURL(file)
    editProofFile.value = await compressImage(file)
  }
}

function removeEditProof() {
  editProofFile.value = null
  editProofPreview.value = null
}

function openEdit(history) {
  editTarget.value = history
  editForm.value = {
    target_date: history.target_date_raw,
    amount: history.amount,
    notes: history.notes || '',
    is_cash: history.is_cash,
    bank_id: history.bank_id
  }
  editProofPreview.value = history.proof_url
  editProofFile.value = null
  showEdit.value = true
}
function closeEdit() {
  showEdit.value = false
  editTarget.value = null
  editProofFile.value = null
  editProofPreview.value = null
}

function submitEdit() {
  editLoading.value = true
  const payload = {
    ...editForm.value,
    amount: parseFloat(editForm.value.amount) || 0,
    bank_id: editForm.value.is_cash ? null : editForm.value.bank_id
  }
  if (editProofFile.value) {
    payload.proof = editProofFile.value
  }
  const formKey = `${type.value}_history`
  const formData = {}
  formData[formKey] = payload
  useForm(formData).patch(`${apiPath.value}/${expense.value.id}/histories/${editTarget.value.id}`, {
    onSuccess: () => { closeEdit(); fetchData() },
    onFinish: () => { editLoading.value = false },
  })
}

// ── Delete ───────────────────────────────────────────────
const showDeleteConfirm = ref(false)
const deleteTarget = ref(null)

function openDeleteConfirm(history) { deleteTarget.value = history; showDeleteConfirm.value = true }
function closeDeleteConfirm() { showDeleteConfirm.value = false; deleteTarget.value = null }

function confirmDelete() {
  return new Promise((resolve) => {
    router.delete(`${apiPath.value}/${expense.value.id}/histories/${deleteTarget.value.id}`, {
      onSuccess: () => { closeDeleteConfirm(); fetchData(); resolve() },
      onError: () => { resolve() },
    })
  })
}
</script>
